import { create } from 'zustand';
import type { UIMessage } from '@/lib/ivyChat/messageParser';
import { processSDKMessage, clearMessageParserCache } from '@/lib/ivyChat/messageParser';
import * as api from '@/services/ivyChatApi';
import { readPaywall402, withPaywallCards, outOfCreditsMessage } from '@/services/billingPaywallDetector';
import { isImageReadNote, refusalMessage, storedFailureMessage, streamErrorMessage } from '@/lib/ivyChat/chatNotices';
import { getQualityTier } from '@/hooks/useQualityTier';

function isCompactionSummary(text: string): boolean {
  if (!text.includes('continued from a previous conversation')) return false;
  return text.includes('summary below covers') || text.includes('Summary:');
}

function extractCompactionText(text: string): string {
  let cleaned = text
    .replace(/<div[^>]*>/gi, '')
    .replace(/<\/div>/gi, '')
    .replace(/<span[^>]*>/gi, '')
    .replace(/<\/span>/gi, '')
    .trim();
  const summaryIdx = cleaned.indexOf('Summary:');
  if (summaryIdx !== -1) {
    cleaned = cleaned.slice(summaryIdx + 'Summary:'.length).trim();
  }
  return cleaned;
}

/** Content-based detection of sub-agent prompts loaded from DB */
function isSubAgentPrompt(text: string): boolean {
  if (text.length < 400) return false;
  if (/\/tmp\/\.ivy\/[a-f0-9-]{20,}\/slides\/slide-\d{3}/.test(text)) return true;
  if (/(?:Redesign|Build|Edit|Generate|Create|Update|Fix)\s+slide-\d{3}\s+for\s+project\s+[a-f0-9]{8}-/i.test(text)) return true;
  const agentHeaders = ['## What to change', '## Files to read', '## Files to write', '## Critical rules'];
  if (agentHeaders.filter(h => text.includes(h)).length >= 2) return true;
  return false;
}

/** Strip auto-injected context prefix from user messages loaded from DB */
function stripContextPrefix(content: string): string {
  // Context format: [project_id: ...] [project: "..."] [view: ...] [action: ...] ...\n\n<user message>
  const match = content.match(/^(\[(?:project_id|project|view|active_slide|action|Context)[^\]]*\]\s*)+\n\n/);
  return match ? content.slice(match[0].length) : content;
}

/** Fired (window event) when the UI itself publishes or finishes an export for a project. */
export const LEARN_TRIGGER_EVENT = 'ivy-learn-trigger';

export function announceLearnTrigger(conversationId: string): void {
  window.dispatchEvent(new CustomEvent(LEARN_TRIGGER_EVENT, { detail: { conversationId } }));
}

// Module-level abort controller — cancelled when switching chats to stop stale stream readers
let activeStreamAbort: AbortController | null = null;

function cancelActiveStream() {
  if (activeStreamAbort) {
    activeStreamAbort.abort();
    activeStreamAbort = null;
  }
}

interface EmbeddedIvyChatState {
  chatId: string | null;
  messages: UIMessage[];
  isStreaming: boolean;
  /** True once loadMessages has finished for the current chatId. The hero auto-send
   *  waits on this so it can't fire before history loads (which would clobber it). */
  messagesLoaded: boolean;
  currentRequestId: string | null;
  eventIndex: number;
  pendingAttachments: string | null;
  pendingVoice: string | null;
  /** The home box's tags on a message Stop handed back to the composer: the next send carries them again. */
  pendingTags: string | null;
  /** Kind of the turn in flight; a 'learn' turn blocks the composer but renders no transcript. */
  activeTurnKind: api.TurnKind | null;

  setChatId: (chatId: string) => void;
  loadMessages: (chatId: string) => Promise<void>;
  sendMessage: (message: string, systemContext?: string) => Promise<void>;
  abortStream: () => Promise<void>;
  checkAndReconnect: (chatId: string) => Promise<void>;
  startLearnTurn: () => Promise<void>;
  checkLearnPending: (conversationId: string) => Promise<void>;
  setPendingAttachments: (ctx: string | null) => void;
  setPendingVoice: (voice: string | null) => void;
  setPendingTags: (tags: string | null) => void;
  reset: () => void;
}

function processNdjsonStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  ownerChatId: string,
  set: (fn: (state: EmbeddedIvyChatState) => Partial<EmbeddedIvyChatState>) => void,
  get: () => EmbeddedIvyChatState,
  signal: AbortSignal,
  render = true,
) {
  const decoder = new TextDecoder();
  let buffer = '';
  let lastEventTime = Date.now();
  let madeWork = false;

  // Inactivity watchdog — if no events arrive for 30s, check server stream status
  // and break the pump if the stream is no longer active. Prevents permanent
  // "Thinking..." state when the HTTP stream hangs (proxy keep-alive, network issues).
  const INACTIVITY_TIMEOUT_MS = 30_000;
  let inactivityTimer: ReturnType<typeof setInterval> | null = null;

  function startInactivityWatchdog() {
    inactivityTimer = setInterval(async () => {
      if (Date.now() - lastEventTime > INACTIVITY_TIMEOUT_MS) {
        try {
          const status = await api.getStreamStatus(ownerChatId);
          if (!status.active) {
            console.warn('[ivyChat] Stream inactive on server — forcing pump exit');
            reader.cancel().catch(() => {});
          }
        } catch {
          // Server unreachable — cancel reader to unblock the pump
          reader.cancel().catch(() => {});
        }
      }
    }, 10_000);
  }

  function stopInactivityWatchdog() {
    if (inactivityTimer) {
      clearInterval(inactivityTimer);
      inactivityTimer = null;
    }
  }

  async function pump(): Promise<void> {
    startInactivityWatchdog();
    try {
      while (true) {
        if (signal.aborted || get().chatId !== ownerChatId) {
          reader.cancel().catch(() => {});
          return;
        }

        const { done, value } = await reader.read();
        if (done) break;

        lastEventTime = Date.now();

        if (signal.aborted || get().chatId !== ownerChatId) {
          reader.cancel().catch(() => {});
          return;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let receivedDone = false;
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const parsed = JSON.parse(line);

            // Backend sends { type: 'done' } when the Claude process exits.
            // Break immediately rather than waiting for HTTP stream EOF.
            if (parsed.type === 'done') {
              receivedDone = true;
              break;
            }

            if (!render) continue;

            if (parsed.type === 'claude_json' && parsed.data) {
              const uiMessages = processSDKMessage(parsed.data as Record<string, unknown>);
              if (uiMessages.some((m) => m.type === 'chat' || m.type === 'tool_use')) madeWork = true;
              if (uiMessages.length > 0) {
                set(state => ({
                  messages: [...state.messages, ...withPaywallCards(state.messages, uiMessages)],
                  eventIndex: state.eventIndex + 1,
                }));
              }
            } else if (parsed.type === 'error') {
              set(state => ({
                messages: [...state.messages, {
                  id: `msg-error-${Date.now()}`,
                  type: 'system' as const,
                  content: streamErrorMessage(parsed, madeWork),
                  timestamp: Date.now(),
                  subtype: 'error',
                  turnFailed: true,
                }],
              }));
            }
          } catch {
            // Skip malformed NDJSON
          }
        }

        if (receivedDone) break;
      }
    } finally {
      stopInactivityWatchdog();
    }
  }

  return pump();
}

export const useEmbeddedIvyChatStore = create<EmbeddedIvyChatState>((set, get) => ({
  chatId: null,
  messages: [],
  isStreaming: false,
  messagesLoaded: false,
  currentRequestId: null,
  eventIndex: 0,
  pendingAttachments: null,
  pendingVoice: null,
  pendingTags: null,
  activeTurnKind: null,

  setPendingAttachments: (ctx: string | null) => set({ pendingAttachments: ctx }),

  setPendingVoice: (voice: string | null) => set({ pendingVoice: voice }),

  setPendingTags: (tags: string | null) => set({ pendingTags: tags }),

  setChatId: (chatId: string) => {
    cancelActiveStream();
    set({ chatId, messages: [], isStreaming: false, activeTurnKind: null, currentRequestId: null, eventIndex: 0, messagesLoaded: false, pendingTags: null });
    clearMessageParserCache();
  },

  loadMessages: async (chatId: string) => {
    clearMessageParserCache();
    try {
      const dbMessages = await api.getMessages(chatId);
      const uiMessages: UIMessage[] = dbMessages.filter((m) => {
        // Filter out sub-agent prompts that were written to DB
        if (m.role === 'user' && isSubAgentPrompt(m.content)) return false;
        // A tool's image-read note, stored as a user message: not the person's words
        if (m.role === 'user' && isImageReadNote(m.content)) return false;
        return true;
      }).map((m, i) => {
        const failure = storedFailureMessage(m, `msg-${i}`);
        if (failure) return failure;
        const content = m.role === 'user' ? stripContextPrefix(m.content) : m.content;
        // Detect compaction summaries stored as user messages in the DB
        if (m.role === 'user' && isCompactionSummary(content)) {
          return {
            id: m.id || `msg-${i}`,
            type: 'system' as const,
            content: extractCompactionText(content),
            timestamp: new Date(m.createdAt).getTime(),
            subtype: 'compaction_summary',
          };
        }
        return {
          id: m.id || `msg-${i}`,
          type: m.role === 'user' ? 'chat' as const
            : m.role === 'assistant' ? 'chat' as const
            : m.role === 'tool_use' ? 'tool_use' as const
            : m.role === 'tool_result' ? 'tool_result' as const
            : m.role === 'result' ? 'result' as const
            : 'system' as const,
          role: (m.role === 'user' ? 'user' : m.role === 'assistant' ? 'assistant' : undefined) as any,
          content,
          timestamp: new Date(m.createdAt).getTime(),
          toolName: m.toolName || undefined,
          toolInput: m.toolInput || undefined,
          toolUseId: m.toolUseId || undefined,
          isError: m.isError,
          costUsd: m.costUsd || undefined,
          durationMs: m.durationMs || undefined,
          inputTokens: m.inputTokens || undefined,
          outputTokens: m.outputTokens || undefined,
        };
      });
      set({ messages: withPaywallCards([], uiMessages), eventIndex: uiMessages.length, messagesLoaded: true });
    } catch {
      // No messages yet — still mark loaded so the auto-send gate doesn't deadlock.
      set({ messagesLoaded: true });
    }
  },

  sendMessage: async (message: string, systemContext?: string) => {
    if (get().isStreaming) return;

    const { chatId } = get();
    if (!chatId) return;

    const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Display the raw user text in the UI (no context prefix)
    const userMessage: UIMessage = {
      id: `msg-user-${Date.now()}`,
      type: 'chat',
      role: 'user',
      content: message,
      timestamp: Date.now(),
    };

    // Send context + message to Claude (context is hidden from UI)
    const apiMessage = systemContext
      ? `${systemContext}\n\n${message}`
      : message;

    set(state => ({
      messages: [...state.messages, userMessage],
      isStreaming: true,
      activeTurnKind: 'normal',
      currentRequestId: requestId,
    }));

    // Create a new abort controller for this stream
    cancelActiveStream();
    const abort = new AbortController();
    activeStreamAbort = abort;

    try {
      const res = await api.sendChatMessage(chatId, apiMessage, requestId, getQualityTier(), abort.signal);

      if (res.status === 404) {
        set(state => ({
          messages: [...state.messages, {
            id: `msg-error-${Date.now()}`,
            type: 'system' as const,
            content: 'Session not found. Please refresh the page.',
            timestamp: Date.now(),
            subtype: 'error',
          }],
        }));
        return;
      }

      if (res.status === 402) {
        const paywall = await readPaywall402(res);
        if (paywall) set(state => ({ messages: [...state.messages, outOfCreditsMessage(paywall)] }));
        return;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        set(state => ({
          messages: [...state.messages, {
            id: `msg-error-${Date.now()}`,
            type: 'system' as const,
            content: refusalMessage(res.status, body, res.headers.get('Retry-After')),
            timestamp: Date.now(),
            subtype: 'error',
          }],
        }));
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) return;

      await processNdjsonStream(reader, chatId, set, get, abort.signal);
    } catch (err) {
      if ((err as Error).name !== 'AbortError' && get().chatId === chatId) {
        console.warn('[ivyChat] Stream broke, attempting reconnect:', (err as Error).message);
        // Stream broke (HTTP/2 reset, network error, proxy timeout).
        // The Claude process may still be running on the backend — the event
        // buffer holds all events. Try to reconnect first; if the stream is
        // already done, fall back to loading messages from the DB.
        try {
          const status = await api.getStreamStatus(chatId);
          if (status.active) {
            console.log('[ivyChat] Backend stream still active, reconnecting...');
            const reconnAbort = new AbortController();
            activeStreamAbort = reconnAbort;
            const reconnRes = await api.reconnectToStream(chatId, get().eventIndex);
            const reconnReader = reconnRes.body?.getReader();
            if (reconnReader) {
              await processNdjsonStream(reconnReader, chatId, set, get, reconnAbort.signal);
              console.log('[ivyChat] Reconnect stream completed');
            }
          } else {
            console.log('[ivyChat] Backend stream done, loading messages from DB');
            await get().loadMessages(chatId);
          }
        } catch {
          // Reconnect failed — load from DB as last resort
          try {
            await get().loadMessages(chatId);
          } catch {
            set(state => ({
              messages: [...state.messages, {
                id: `msg-error-${Date.now()}`,
                type: 'system' as const,
                content: 'Connection lost. Please refresh if messages are missing.',
                timestamp: Date.now(),
                subtype: 'error',
              }],
            }));
          }
        }
      }
    } finally {
      // Only clear streaming state if this is still the active stream: after a Stop, a new message may be running
      if (get().chatId === chatId && get().currentRequestId === requestId) {
        set({ isStreaming: false, activeTurnKind: null, currentRequestId: null });
      }
    }
  },

  abortStream: async () => {
    const { currentRequestId } = get();
    if (currentRequestId) {
      cancelActiveStream();
      set({ isStreaming: false, activeTurnKind: null, currentRequestId: null });
      await api.abortRequest(currentRequestId);
    }
  },

  checkAndReconnect: async (chatId: string) => {
    // Skip if already streaming (prevents duplicate processing when
    // multiple EmbeddedIvyChat instances share this store)
    if (get().isStreaming) return;
    try {
      const status = await api.getStreamStatus(chatId);
      // Re-check after async call — sendMessage may have started while we waited
      if (get().isStreaming) return;
      if (get().chatId !== chatId) return;
      if (status.active) {
        cancelActiveStream();
        const abort = new AbortController();
        activeStreamAbort = abort;

        const learn = status.turnKind === 'learn';
        const requestId = status.requestId ?? null;
        set({ isStreaming: true, activeTurnKind: learn ? 'learn' : 'normal', currentRequestId: requestId });
        const res = await api.reconnectToStream(chatId, learn ? 0 : get().eventIndex);
        const reader = res.body?.getReader();
        if (!reader) {
          set({ isStreaming: false, activeTurnKind: null, currentRequestId: null });
          return;
        }
        await processNdjsonStream(reader, chatId, set, get, abort.signal, !learn);
        if (get().chatId === chatId && get().currentRequestId === requestId) {
          set({ isStreaming: false, activeTurnKind: null, currentRequestId: null });
        }
      }
    } catch {
      // No active stream
    }
  },

  startLearnTurn: async () => {
    const { chatId, isStreaming } = get();
    if (!chatId || isStreaming) return;

    const requestId = `learn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    set({ isStreaming: true, activeTurnKind: 'learn', currentRequestId: requestId });
    cancelActiveStream();
    const abort = new AbortController();
    activeStreamAbort = abort;

    try {
      const res = await api.sendLearnTurn(chatId, requestId);
      const reader = res.ok ? res.body?.getReader() : undefined;
      if (reader) await processNdjsonStream(reader, chatId, set, get, abort.signal, false);
    } catch (err) {
      console.warn('[ivyChat] Learn turn stream ended early:', (err as Error).message);
    } finally {
      if (get().chatId === chatId && get().currentRequestId === requestId) {
        set({ isStreaming: false, activeTurnKind: null, currentRequestId: null });
      }
    }
  },

  checkLearnPending: async (conversationId: string) => {
    if (get().isStreaming) return;
    const session = await api.getSessionByConversation(conversationId).catch(() => null);
    if (!session?.learnPending || session.chatId !== get().chatId) return;
    await get().startLearnTurn();
  },

  reset: () => {
    cancelActiveStream();
    set({ chatId: null, messages: [], isStreaming: false, activeTurnKind: null, messagesLoaded: false, currentRequestId: null, eventIndex: 0, pendingAttachments: null, pendingVoice: null, pendingTags: null });
    clearMessageParserCache();
  },
}));
